"use client";

import * as React from "react";
import { FileText } from "lucide-react";
import type { Citation } from "@quovy/field-types";
import { Button } from "../../../inputs/button";
import { CitationHoverCard, type CitationHoverData } from "../../citations/citation-hover-card";
import { cn } from "../../../utils";
import { useLazyOverlay } from "./use-lazy-overlay";

export interface CitationBadgeProps {
  citations: Citation[];
  /** Citation id treated as primary; defaults to first. */
  primaryCitationId?: string;
  /** Opens the file-viewer drawer scoped to this cell on the citations tab. */
  onOpenInDrawer?: () => void;
  className?: string;
}

function toHoverData(c: Citation): CitationHoverData {
  const out: CitationHoverData = {
    id: c.id,
    filename: c.label,
    citedText: c.snippet ?? "",
  };
  if (c.page !== undefined) out.pageNumber = c.page;
  if (c.bbox) {
    out.boundingBox = {
      left: c.bbox[0],
      top: c.bbox[1],
      width: c.bbox[2],
      height: c.bbox[3],
      page: c.page ?? 1,
    };
  }
  return out;
}

/**
 * Citation count badge that opens a `CitationHoverCard` preview of the
 * primary citation. Clicking the badge invokes `onOpenInDrawer` (the grid
 * is expected to surface the file viewer there).
 */
export function CitationBadge({
  citations,
  primaryCitationId,
  onOpenInDrawer,
  className,
}: CitationBadgeProps) {
  const { primed, triggerProps } = useLazyOverlay();

  if (!citations || citations.length === 0) return null;
  const primary =
    (primaryCitationId && citations.find((c) => c.id === primaryCitationId)) || citations[0]!;
  const hoverData = citations.map(toHoverData);
  const count = citations.length;
  const displayCount = count > 99 ? "99+" : String(count);

  const button = (
    <Button
      variant="ghost"
      type="button"
      {...triggerProps}
      onClick={(e) => {
        e.stopPropagation();
        onOpenInDrawer?.();
      }}
      aria-label={`${count} citation${count === 1 ? "" : "s"}`}
      className={cn(
        "h-5 shrink-0 gap-0.5 rounded px-1 text-[11px] text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        className,
      )}
    >
      <FileText className="size-3" aria-hidden="true" />
      <span className="font-medium leading-none">{displayCount}</span>
    </Button>
  );

  if (!primed) return button;

  return (
    <CitationHoverCard
      citations={hoverData}
      usedCitationId={primary.id}
      initialCitationId={primary.id}
      {...(onOpenInDrawer ? { onOpenInDrawer: () => onOpenInDrawer() } : {})}
    >
      {button}
    </CitationHoverCard>
  );
}
